import React, { useState, useEffect, useMemo } from 'react';
import apiClient from '../ApiClient';
import { notify } from '../utils/ToastUtils';
import moment from 'moment';
import { useSelector } from "react-redux"
import { selectEmployees } from 'src/stores/employeeSlice';
import "../css/DSRAbsentSA.css";
import CentralizedTable from '../components/CentralizedTable';
import UserAvatar from '../components/UserAvatar';
import { ListCardSkeleton, CalendarSkeleton } from '../components/PageSkeletonLoaders';
import MonthPicker from '../components/MonthPicker';
import { Line } from 'react-chartjs-2';
import { X } from 'lucide-react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { 
    NotificationsNoneOutlined, 
    CalendarTodayOutlined, 
    VisibilityOutlined, 
    PeopleAltOutlined, 
    CheckCircleOutline, 
    ErrorOutline, 
    EventNoteOutlined, 
    CelebrationOutlined,
    FilterAltOutlined,
    RestartAltOutlined,
    ChevronLeft,
    ChevronRight,
    Search,
    TrendingUpOutlined,
    DonutLargeOutlined
} from '@mui/icons-material';
import { Select, MenuItem, TextField, Autocomplete, Chip, Skeleton } from '@mui/material';
import { ALL_LOCATIONS } from 'src/utils/constants';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

function DSRAbsentSA() {
    const employeesList = useSelector(selectEmployees)
    const user = useSelector((state) => state.user);
    const managerMail = user?.role === "Manager" ? user?.mail : null;

    const [state, setState] = useState({
        startDate: moment().startOf('month').format('YYYY-MM-DD'),
        endDate: moment().endOf('month').format('YYYY-MM-DD'),
        employees: [],
        location: 'All',
        status: 'All'
    });

    const [appliedFilters, setAppliedFilters] = useState({
        startDate: moment().startOf('month').format('YYYY-MM-DD'),
        endDate: moment().endOf('month').format('YYYY-MM-DD'),
        location: 'All',
        status: 'All'
    });

    const [isDisabled, setIsDisabled] = useState(false);
    
    // Dashboard Data States
    const [trendData, setTrendData] = useState([]);
    const [calendarData, setCalendarData] = useState([]);
    const [stats, setStats] = useState({ totalEmployees: 0, submittedDsr: 0, missingDsr: 0, onLeave: 0, holiday: 0 });
    const [tableData, setTableData] = useState([]);
    
    // Calendar view state
    const [calendarMonth, setCalendarMonth] = useState(moment());
    const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);

    const selectMenuProps = {
        PaperProps: {
            style: {
                maxHeight: 250,
                maxWidth: 10,
            },
            sx: {
                '& .MuiMenuItem-root': {
                    fontSize: '14px'
                }
            }
        },
    };

    const handleChange = (evt) => {
        const { name, value } = evt.target;
        if (name === "location") {
            setState({
                ...state,
                location: value,
                employees: []
            });
        } else {
            setState({
                ...state,
                [name]: value
            });
        }
    };


    const getDashboardData = async (overrideStartDate, overrideEndDate) => {
        const startDt = (typeof overrideStartDate === 'string') ? overrideStartDate : state.startDate;
        const endDt = (typeof overrideEndDate === 'string') ? overrideEndDate : state.endDate;
        
        setIsDisabled(true);
        try {
            const { data } = await apiClient.get(`${process.env.REACT_APP_BASE_URL}/admin/getDsrDashboardData`, {
                params: {
                    startDt: startDt,
                    endDt: endDt,
                    managerMail: managerMail,
                    location: state.location,
                    filterEmpNames: state.employees.length > 0 ? state.employees.map(e => e.EMP_NAME || e.emp_name).join(',') : undefined
                }
            });
            
            setTrendData(data.trendData || []);
            setCalendarData(data.calendarData || []);
            setStats(data.stats || { totalEmployees: 0, submittedDsr: 0, missingDsr: 0, onLeave: 0, holiday: 0 });
            setTableData(data.tableData || []);
            setAppliedFilters({ 
                startDate: startDt, 
                endDate: endDt, 
                location: state.location, 
                status: state.status 
            });
            if (!overrideStartDate) {
                setCalendarMonth(moment(startDt, 'YYYY-MM-DD'));
            }
        } catch (error) {
            console.log(error);
            let errmsg = (error?.response) ? (error?.response?.data?.message ? error?.response?.data?.message : `${error?.response?.status} - ${error?.response?.statusText}`) : "Something went wrong. Please try again later"
            notify.error(errmsg);
        } finally {
            setIsDisabled(false);
        }
    };

    useEffect(() => {
        getDashboardData();
    }, []);

    // Filter out weekends and days with no activity (like holidays) to avoid artificial drops
    const workingTrendData = trendData.filter(d => {
        const day = moment(d.date).day();
        const isWeekend = day === 0 || day === 6;
        // Also skip if it's a weekday but there are completely 0 submissions/missing/leaves (likely a holiday)
        const isHoliday = d.submitted === 0 && d.missing === 0 && d.leave === 0;
        return !isWeekend && !isHoliday;
    });

    // --- Chart Data Config ---
    const chartData = {
        labels: workingTrendData.map(d => moment(d.date).format('DD MMM')),
        datasets: [
            {
                label: 'Submitted DSR',
                data: workingTrendData.map(d => d.submitted),
                borderColor: '#3b82f6',
                borderWidth: 2,
                backgroundColor: (context) => {
                    const chart = context.chart;
                    const { ctx, chartArea } = chart;
                    if (!chartArea) return 'rgba(59, 130, 246, 0.1)';
                    const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
                    gradient.addColorStop(0, 'rgba(59, 130, 246, 0.35)');
                    gradient.addColorStop(1, 'rgba(59, 130, 246, 0.0)');
                    return gradient;
                },
                fill: true,
                tension: 0.4,
                pointBackgroundColor: '#3b82f6',
                pointBorderColor: '#ffffff',
                pointHoverBackgroundColor: '#ffffff',
                pointHoverBorderColor: '#3b82f6',
                pointRadius: 4,
                pointHoverRadius: 6,
            },
            {
                label: 'Missing DSR',
                data: workingTrendData.map(d => d.missing),
                borderColor: '#f43f5e',
                borderWidth: 2,
                backgroundColor: (context) => {
                    const chart = context.chart;
                    const { ctx, chartArea } = chart;
                    if (!chartArea) return 'rgba(244, 63, 94, 0.1)';
                    const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
                    gradient.addColorStop(0, 'rgba(244, 63, 94, 0.35)');
                    gradient.addColorStop(1, 'rgba(244, 63, 94, 0.0)');
                    return gradient;
                },
                fill: true,
                tension: 0.4,
                pointBackgroundColor: '#f43f5e',
                pointBorderColor: '#ffffff',
                pointHoverBackgroundColor: '#ffffff',
                pointHoverBorderColor: '#f43f5e',
                pointRadius: 4,
                pointHoverRadius: 6,
                borderDash: []
            },
            {
                label: 'On Leave',
                data: workingTrendData.map(d => d.leave),
                borderColor: 'transparent',
                backgroundColor: 'transparent',
                fill: false,
                tension: 0,
                pointBackgroundColor: '#f59e0b',
                pointBorderColor: 'transparent',
                pointHoverBackgroundColor: '#f59e0b',
                pointHoverBorderColor: '#ffffff',
                pointRadius: 0,
                pointHoverRadius: 6,
                borderWidth: 0
            }
        ]
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'top',
                align: 'end',
                labels: { 
                    usePointStyle: true, 
                    font: { size: 11 },
                    padding: 16,
                    filter: function(item, chart) {
                        return item.text !== 'On Leave';
                    }
                }
            },
            tooltip: {
                mode: 'index',
                intersect: false,
                callbacks: {
                    label: function(context) {
                        return ` ${context.dataset.label} : ${context.raw}`;
                    },
                    title: function(context) {
                        return moment(context[0].label, 'DD MMM').format('DD MMM YYYY');
                    }
                }
            },
            datalabels: {
                display: false
            }
        },
        scales: {
            y: { beginAtZero: true, grid: { borderDash: [5, 5] } },
            x: { 
                grid: { display: false },
                ticks: {
                    maxRotation: 0,
                    minRotation: 0,
                    maxTicksLimit: 6
                }
            }
        }
    };

    // --- Table & Filtering ---
    const filteredTableData = useMemo(() => {
        return tableData.filter(row => {
            let match = true;
            if (appliedFilters.location !== 'All' && row.location !== appliedFilters.location) match = false;
            if (appliedFilters.status !== 'All' && row.status !== appliedFilters.status) match = false;
            return match;
        });
    }, [tableData, appliedFilters.location, appliedFilters.status]);

    // --- Table Columns ---
    const columns = useMemo(() => [
        {
            headerName: "Employee",
            field: "empName",
            flex: 1.5,
            minWidth: 130,
            renderCell: (params) => (
                <div className="table-emp-cell flex items-center gap-3 w-full min-w-0">
                    <div className="shrink-0">
                        <UserAvatar 
                            name={params.row.empName} 
                            imageUrl={params.row.imgPath ? `${process.env.REACT_APP_BASE_URL}/uploads/${params.row.imgPath}` : null} 
                            size={32} 
                        />
                    </div>
                    <div className="flex flex-col min-w-0 flex-1 overflow-hidden">
                        <div className="emp-name truncate w-full font-medium" title={params.row.empName}>{params.row.empName}</div>
                        <span className="text-[11px] text-gray-500 -mt-0.5 truncate w-full" title={params.row.emp_id}>{params.row.emp_id}</span>
                    </div>
                </div>
            )
        },
        { headerName: "Location", field: "location", flex: 0.8, minWidth: 70 },
        {
            headerName: "Missing Days",
            field: "missingDays",
            flex: 0.9,
            minWidth: 100,
            renderCell: (params) => {
                const days = parseInt(params.row.missingDays) || 0;
                return (
                    <span className={`text-[13px] font-bold ${days > 0 ? 'text-red-600' : 'text-slate-500'}`}>
                        {days > 0 ? days : '-'}
                    </span>
                );
            }
        },
        {
            headerName: "DSR Status",
            field: "status",
            flex: 1.2,
            minWidth: 130,
            renderCell: (params) => {
                const hasMissing = params.row.missingDays && params.row.missingDays !== '-';
                if (hasMissing) {
                    return (
                        <div className="flex items-center gap-1.5 bg-red-50 text-red-700 px-2.5 py-1 rounded-full border border-red-100 w-fit">
                            <div className="w-2 h-2 rounded-full bg-red-500"></div>
                            <span className="text-[12px] font-bold">Missing</span>
                        </div>
                    );
                } else {
                    return (
                        <div className="flex items-center gap-1.5 bg-green-50 text-green-700 px-2.5 py-1 rounded-full border border-green-100 w-fit">
                            <div className="w-2 h-2 rounded-full bg-green-500"></div>
                            <span className="text-[12px] font-bold">Submitted</span>
                        </div>
                    );
                }
            }
        },
        { 
            headerName: "Last Submitted", 
            field: "lastSubmittedDsr", 
            flex: 1.2, 
            minWidth: 110,
            renderCell: (params) => {
                const val = params.value;
                if (!val) return <span className="text-[13px] text-gray-600 font-medium">-</span>;
                // If it looks like '24 Jul 2026', just simplify it or show as is
                return <span className="text-[14px] text-slate-700 font-medium">{val.replace(' 2026', '')}</span>;
            }
        }
    ], [filteredTableData]);

    // Handle initial selected employee when data loads
    useEffect(() => {
        if (filteredTableData.length > 0 && (!selectedEmployeeId || !filteredTableData.find(e => e.empCode === selectedEmployeeId))) {
            setSelectedEmployeeId(filteredTableData[0].empCode);
        }
    }, [filteredTableData, selectedEmployeeId]);

    // --- Calendar Month Navigation Helpers ---
    const handleMonthChange = (targetMonth) => {
        if (isDisabled) return;
        
        const appliedStartMonth = appliedFilters.startDate ? moment(appliedFilters.startDate, 'YYYY-MM-DD').startOf('month') : null;
        const appliedEndMonth = appliedFilters.endDate ? moment(appliedFilters.endDate, 'YYYY-MM-DD').endOf('month') : null;
        const targetMonthStart = targetMonth.clone().startOf('month');
        const targetMonthEnd = targetMonth.clone().endOf('month');

        // If target month is already within the fetched date range, switch month locally without hitting API
        const isWithinLoadedRange = appliedStartMonth && appliedEndMonth && 
            targetMonthStart.isSameOrAfter(appliedStartMonth, 'month') && 
            targetMonthStart.isSameOrBefore(appliedEndMonth, 'month');

        if (isWithinLoadedRange) {
            setCalendarMonth(targetMonth);
        } else {
            // Exceeds loaded range: expand date range in the respective direction keeping existing frame data
            let newStart = state.startDate;
            let newEnd = state.endDate;

            if (!appliedStartMonth || targetMonthStart.isBefore(appliedStartMonth, 'month')) {
                newStart = targetMonthStart.format('YYYY-MM-DD');
            }
            if (!appliedEndMonth || targetMonthEnd.isAfter(appliedEndMonth, 'month')) {
                newEnd = targetMonthEnd.format('YYYY-MM-DD');
            }
            
            setState(prev => ({
                ...prev,
                startDate: newStart,
                endDate: newEnd
            }));
            setCalendarMonth(targetMonth);
            getDashboardData(newStart, newEnd);
        }
    };

    const prevMonth = () => {
        handleMonthChange(calendarMonth.clone().subtract(1, 'month'));
    };

    const nextMonth = () => {
        handleMonthChange(calendarMonth.clone().add(1, 'month'));
    };

    const goToToday = () => {
        handleMonthChange(moment());
    };

    return (
        <div className="space-y-3">
            {/* Filters */}
            <div className="bg-white shadow-sm rounded-xl border border-slate-200 p-5 mb-3">
                <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 border-b border-slate-100 pb-5 mb-5">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center shrink-0">
                            <NotificationsNoneOutlined />
                        </div>
                        <div className="flex flex-col items-start justify-center min-w-0">
                            <h2 className="text-[16px] font-bold text-slate-800 m-0 leading-tight">DSR Compliance</h2>
                            <p className="text-xs text-slate-500 font-medium mt-0.5 block m-0">Track missing submissions, approved leaves and attendance trend.</p>
                        </div>
                    </div>
                    <div className="flex items-center bg-slate-50 border border-slate-200 px-3 py-1.5 rounded-lg shrink-0 mt-3 lg:mt-0">
                        <span className="text-xs font-bold text-slate-700">
                            {moment(state.startDate).format('DD MMM YYYY')} - {moment(state.endDate).format('DD MMM YYYY')}
                        </span>
                    </div>
                </div>
                <div className="flex flex-col gap-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-[1fr_1fr_1fr_1fr_auto] gap-4 items-end">
                        <div className="w-full min-w-0">
                            <label className="block text-sm font-semibold text-slate-700 mb-1">Start Date</label>
                            <TextField
                                type="date"
                                size="small"
                                className="w-full bg-white"
                                value={state.startDate}
                                onChange={(e) => setState({ ...state, startDate: e.target.value })}
                                InputLabelProps={{ shrink: true }}
                            />
                        </div>
                        <div className="w-full min-w-0">
                            <label className="block text-sm font-semibold text-slate-700 mb-1">End Date</label>
                            <TextField
                                type="date"
                                size="small"
                                className="w-full bg-white"
                                value={state.endDate}
                                onChange={(e) => setState({ ...state, endDate: e.target.value })}
                                InputLabelProps={{ shrink: true }}
                            />
                        </div>
                        <div className="w-full min-w-0">
                            <label className="block text-sm font-semibold text-slate-700 mb-1">Location</label>
                            <Select
                                name="location"
                                value={state.location}
                                onChange={handleChange}
                                className="w-full bg-white text-sm"
                                size="small"
                                MenuProps={selectMenuProps}
                                renderValue={(selected) => selected === "All" ? "All Locations" : selected}
                                sx={{
                                    '& .MuiSelect-select': {
                                        paddingRight: '32px !important',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap'
                                    }
                                }}
                            >
                                <MenuItem value="All" style={{ whiteSpace: "normal" }}>
                                    <div className="text-[13px] leading-snug whitespace-normal break-words">All Locations</div>
                                </MenuItem>
                                {ALL_LOCATIONS?.map(loc => (
                                    <MenuItem key={loc.value} value={loc.value}>{loc.label}</MenuItem>
                                ))}
                            </Select>
                        </div>

                        <div className="w-full min-w-0">
                            <label className="block text-sm font-semibold text-slate-700 mb-1">Employee</label>
                            <Autocomplete
                                multiple
                                options={(employeesList || [])?.filter(emp => emp?.STATUS === "Active" && emp?.DSR_EXCEPTION !== "Yes" && (state.location === 'All' || emp?.EMP_COUNTRY === state?.location))}
                                getOptionLabel={(option) => `${option.EMP_NAME} (${option.EMP_ID})`}
                                value={state.employees}
                                onChange={(event, newValue) => {
                                    setState({ ...state, employees: newValue });
                                }}
                                size="small"
                                className="w-full bg-white text-sm"
                                limitTags={1}
                                disableCloseOnSelect
                                renderTags={() => null}
                                ListboxProps={{ sx: { fontSize: '14px' } }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        height: '40px',
                                        minHeight: '40px',
                                        paddingTop: '0 !important',
                                        paddingBottom: '0 !important',
                                        fontSize: '14px'
                                    },
                                    '& .MuiAutocomplete-input': {
                                        paddingTop: '0 !important',
                                        paddingBottom: '0 !important',
                                    }
                                }}
                                renderInput={(params) => (
                                    <TextField 
                                        {...params} 
                                        placeholder={state.employees.length > 0 ? `${state.employees.length} Selected` : "All Employees"} 
                                    />
                                )}
                            />
                        </div>
                        {/* <div className="w-full min-w-0">
                            <label className="block text-sm font-semibold text-slate-700 mb-1">Status</label>
                            <Select
                                name="status"
                                value={state.status}
                                onChange={handleChange}
                                className="w-full bg-white text-sm"
                                size="small"
                                MenuProps={selectMenuProps}
                                renderValue={(selected) => selected === "All" ? "All Status" : selected}
                                sx={{
                                    '& .MuiSelect-select': {
                                        paddingRight: '32px !important',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap'
                                    }
                                }}
                            >
                                <MenuItem value="All" style={{ whiteSpace: "normal" }}>
                                    <div className="text-[13px] leading-snug whitespace-normal break-words">All Status</div>
                                </MenuItem>
                                <MenuItem value="Submitted DSR" style={{ whiteSpace: "normal" }}>
                                    <div className="text-[13px] leading-snug whitespace-normal break-words">Submitted DSR</div>
                                </MenuItem>
                                <MenuItem value="Missing DSR" style={{ whiteSpace: "normal" }}>
                                    <div className="text-[13px] leading-snug whitespace-normal break-words">Missing DSR</div>
                                </MenuItem>
                                <MenuItem value="On Leave" style={{ whiteSpace: "normal" }}>
                                    <div className="text-[13px] leading-snug whitespace-normal break-words">On Leave</div>
                                </MenuItem>
                                <MenuItem value="Holiday" style={{ whiteSpace: "normal" }}>
                                    <div className="text-[13px] leading-snug whitespace-normal break-words">Holiday</div>
                                </MenuItem>
                            </Select>
                        </div> */}
                        
                        <div className="w-full flex justify-end lg:justify-start gap-3 mt-2 lg:mt-0">
                            <button 
                                onClick={getDashboardData} 
                                className="text-sm h-[40px] px-5 bg-blue-600 text-white rounded-[6px] hover:bg-blue-700 transition-colors font-medium shadow-sm flex items-center justify-center gap-2 whitespace-nowrap"
                                disabled={isDisabled}
                            >
                                {isDisabled ? (
                                    <span className="w-4 h-4 rounded-full border-2 border-white border-t-transparent animate-spin inline-block"></span>
                                ) : (
                                    <FilterAltOutlined style={{ fontSize: 18 }} />
                                )}
                                Apply Filters
                            </button>
                        </div>
                    </div>

                    {/* Selected Employees Chips */}
                    {state.employees.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-slate-100 w-full">
                            <span className="text-[13px] font-semibold text-slate-500 flex items-center">Selected Employees:</span>
                            {state.employees.map((emp) => (
                                <Chip
                                    key={emp.EMP_CODE}
                                    label={emp.EMP_NAME}
                                    onDelete={() => {
                                        setState(prev => ({
                                            ...prev,
                                            employees: prev.employees.filter(e => e.EMP_CODE !== emp.EMP_CODE)
                                        }));
                                    }}
                                    size="small"
                                    deleteIcon={<X size={14} strokeWidth={2.5} />}
                                    sx={{ 
                                        height: 24, 
                                        fontSize: '12px',
                                        backgroundColor: '#eff6ff', // blue-50
                                        color: '#2563eb', // blue-600
                                        fontWeight: 'bold',
                                        '& .MuiChip-deleteIcon': {
                                            color: '#3b82f6', // blue-500
                                            '&:hover': { color: '#1d4ed8' } // blue-700
                                        }
                                    }}
                                />
                            ))}
                            <button 
                                onClick={() => setState(prev => ({ ...prev, employees: [] }))}
                                className="text-[12px] text-red-500 hover:text-red-700 font-medium ml-2 underline underline-offset-2"
                            >
                                Clear All
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Middle Section: Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-[7fr_3fr] gap-3">
                <div className="chart-card trend-chart bg-white rounded-xl border border-slate-200 shadow-sm p-5 flex flex-col h-full w-full min-w-0 overflow-hidden">
                    <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
                            <TrendingUpOutlined />
                        </div>
                        <div className="flex flex-col items-start justify-center flex-1 min-w-0">
                            <h3 className="text-[16px] font-bold text-slate-800 m-0 leading-tight truncate w-full">
                                DSR Submission Trend
                            </h3>
                            <span className="text-xs text-slate-500 font-medium mt-0.5 block truncate w-full">Daily submission vs missing DSR trend</span>
                        </div>
                    </div>
                    <div className="chart-body flex-1 min-h-[250px]">
                        {(isDisabled && trendData.length === 0) ? <div className="h-full w-full"><Skeleton variant="rounded" height="100%" /></div> : <Line data={chartData} options={chartOptions} />}
                    </div>
                </div>

                <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 flex flex-col h-full w-full min-w-0 overflow-hidden">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
                            <DonutLargeOutlined />
                        </div>
                        <div className="flex flex-col items-start justify-center min-w-0">
                            <h3 className="text-[16px] font-bold text-slate-800 m-0 flex items-center gap-2 truncate w-full">
                                DSR Status Overview
                            </h3>
                            <span className="text-xs text-slate-500 font-medium mt-0.5 truncate w-full">Overall submission status of employees</span>
                        </div>
                    </div>
                    {(() => {
                        if (isDisabled && trendData.length === 0) {
                            return <div className="flex-1 w-full"><Skeleton variant="rounded" height="100%" /></div>;
                        }
                        const submittedCount = filteredTableData.filter(row => {
                            const days = parseInt(row.missingDays) || 0;
                            return !row.missingDays || row.missingDays === '-' || days === 0;
                        }).length;
                        
                        const missingCount = filteredTableData.filter(row => {
                            const days = parseInt(row.missingDays) || 0;
                            return row.missingDays && row.missingDays !== '-' && days > 0;
                        }).length;

                        const totalStats = submittedCount + missingCount || 1;
                        const subPct = (submittedCount / totalStats) * 100;
                        const missPct = (missingCount / totalStats) * 100;
                        return (
                            <div className="flex flex-col flex-1 justify-center gap-6">
                                {/* Progress Bar */}
                                <div className="flex h-6 w-full rounded-md overflow-hidden bg-slate-100 mt-2">
                                    {subPct > 0 && <div style={{ width: `${subPct}%` }} className="bg-[#22C55E] transition-all duration-500"></div>}
                                    {missPct > 0 && <div style={{ width: `${missPct}%` }} className="bg-[#EF4444] transition-all duration-500"></div>}
                                </div>
                                
                                {/* Stats Grid */}
                                <div className="grid grid-cols-2 divide-x divide-slate-200 pb-2 mt-2">
                                    {/* Submitted */}
                                    <div className="flex flex-col items-center justify-center text-center px-2">
                                        <div className="flex items-center gap-2 text-[14px] font-bold text-slate-800 mb-3">
                                            <div className="w-2.5 h-2.5 rounded-full bg-[#22C55E]"></div>
                                            Submitted
                                        </div>
                                        <div className="text-[36px] font-bold leading-none mb-2 text-[#22C55E]">{submittedCount}</div>
                                        <div className="text-[13px] font-medium text-slate-500">Employees</div>
                                    </div>
                                    
                                    {/* Missing */}
                                    <div className="flex flex-col items-center justify-center text-center px-2">
                                        <div className="flex items-center gap-2 text-[14px] font-bold text-slate-800 mb-3">
                                            <div className="w-2.5 h-2.5 rounded-full bg-[#EF4444]"></div>
                                            Missing
                                        </div>
                                        <div className="text-[36px] font-bold leading-none mb-2 text-[#EF4444]">{missingCount}</div>
                                        <div className="text-[13px] font-medium text-slate-500">Employees</div>
                                    </div>
                                </div>
                            </div>
                        );
                    })()}
                </div>

            </div>

            {/* Bottom Row: Table and Calendar */}
            <div className="flex flex-col lg:flex-row items-stretch gap-3 mt-4">
                {/* Table Section */}
                <div className="w-full lg:w-[60%] flex flex-col bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                    <div className="flex-1 flex flex-col">
                        <CentralizedTable
                            getRowId={row => row.empCode}
                            rows={filteredTableData}
                            state={{ isLoading: isDisabled && trendData.length === 0 }}
                            columns={columns}
                            initialState={{ pagination: { paginationModel: { pageSize: 10, page: 0 } } }}
                            muiTableContainerProps={{
                                sx: {
                                    height: '580px',
                                    flex: 1,
                                    overflow: 'auto'
                                },
                            }}
                            pageSizeOptions={[10, 25, 50]}
                            hideFooterSelectedRowCount
                            renderTopToolbarCustomActions={() => (
                                <div className="flex items-center gap-3 w-full py-3 ps-1">
                                    <div className="w-10 h-10 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0">
                                        <PeopleAltOutlined />
                                    </div>
                                    <div className="flex flex-col items-start justify-center">
                                        <h3 className="text-[16px] font-bold text-slate-800 m-0 flex items-center gap-2">
                                            Employee DSR Compliance
                                        </h3>
                                        <span className="text-xs text-slate-500 font-medium mt-0.5">Employees requiring attention based on DSR submissions</span>
                                    </div>
                                </div>
                            )}
                            muiTableBodyRowProps={({ row }) => ({
                                onClick: () => setSelectedEmployeeId(row.original.empCode),
                                sx: {
                                    cursor: 'pointer',
                                    ...(selectedEmployeeId === row.original.empCode && {
                                        backgroundColor: '#eff6ff !important',
                                        borderLeft: '4px solid #3b82f6 !important',
                                        boxShadow: 'inset 0 1px 0 #e2e8f0, inset 0 -1px 0 #e2e8f0',
                                    }),
                                    '&:hover': {
                                        backgroundColor: selectedEmployeeId === row.original.empCode ? '#eff6ff !important' : '#f8fafc',
                                    }
                                }
                            })}
                        />
                    </div>
                </div>

                {/* Calendar Section */}
                <div className="w-full lg:w-[40%] flex flex-col bg-white rounded-xl border border-slate-200 shadow-sm p-5 overflow-hidden">
                    {(() => {
                        const currentEmpId = selectedEmployeeId || (filteredTableData.length > 0 ? filteredTableData[0].empCode : null);
                        const selectedEmp = filteredTableData.find(e => e.empCode === currentEmpId);
                        const empCalData = currentEmpId ? (calendarData.find(c => c.empCode === currentEmpId) || { statuses: {} }) : { statuses: {} };
                        const renderCalendarDays = () => {
                            const startOfMonth = calendarMonth.clone().startOf('month');
                            const endOfMonth = calendarMonth.clone().endOf('month');
                            const startDate = startOfMonth.clone().startOf('isoWeek'); // Mon
                            const endDate = endOfMonth.clone().endOf('isoWeek'); // Sun

                            const days = [];
                            let day = startDate.clone();
                            while (day.isSameOrBefore(endDate)) {
                                const dateStr = day.format('YYYY-MM-DD');
                                const st = empCalData.statuses[dateStr];
                                const isCurrentMonth = day.month() === calendarMonth.month();
                                const isToday = day.isSame(moment(), 'day');
                                
                                let pillClass = "bg-slate-100 text-slate-400"; // default No Data
                                let stText = "-";
                                
                                if (isCurrentMonth) {
                                    if (st === 'P') { pillClass = "bg-green-600 text-white"; stText = "P"; }
                                    else if (st === 'A') { pillClass = "bg-red-500 text-white"; stText = "A"; }
                                    else if (st === 'L') { pillClass = "bg-orange-500 text-white"; stText = "L"; }
                                    else if (st === 'H') { pillClass = "bg-purple-500 text-white"; stText = "H"; }
                                }

                                days.push(
                                    <div 
                                        key={dateStr} 
                                        className="flex flex-col items-center justify-center gap-1 h-full py-1"
                                        title={isToday ? `Today (${day.format('DD MMM YYYY')})` : undefined}
                                    >
                                        <div className={`text-[12px] sm:text-[13px] font-semibold flex items-center justify-center ${isToday ? 'text-blue-600 font-bold underline underline-offset-2 decoration-2 decoration-blue-500' : (isCurrentMonth ? 'text-slate-700' : 'text-slate-300')}`}>
                                            {day.date()}
                                        </div>
                                        {isCurrentMonth ? (
                                            <div className={`w-[20px] h-[20px] sm:w-[22px] sm:h-[22px] rounded-full flex items-center justify-center text-[9px] sm:text-[10px] font-bold ${pillClass}`}>
                                                {stText}
                                            </div>
                                        ) : (
                                            <div className="w-[20px] h-[20px] sm:w-[22px] sm:h-[22px]"></div>
                                        )}
                                    </div>
                                );
                                day.add(1, 'day');
                            }
                            return days;
                        };

                        return (
                            <div className="flex flex-col flex-1 overflow-y-auto">
                                {/* Header Row */}
                                <div className="flex flex-nowrap items-start justify-between gap-4 mb-4 pb-4 border-b border-slate-100">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center shrink-0">
                                            <CalendarTodayOutlined />
                                        </div>
                                        <div className="flex flex-col items-start justify-center">
                                            <h3 className="text-[16px] font-bold text-slate-800 m-0 flex items-center gap-2">
                                                Employee Timeline
                                            </h3>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-1.5 shrink-0 mt-1">
                                        <button 
                                            className={`w-7 h-7 rounded-lg border border-slate-200 text-slate-600 transition-colors bg-white shadow-sm flex items-center justify-center ${isDisabled ? 'opacity-40 cursor-not-allowed' : 'hover:bg-slate-50 cursor-pointer'}`} 
                                            onClick={prevMonth}
                                            disabled={isDisabled}
                                            title={isDisabled ? "Loading..." : "Previous Month"}
                                        >
                                            <ChevronLeft className="w-4 h-4" />
                                        </button>
                                        <div className="px-2 h-7 flex items-center justify-center rounded-lg border border-slate-200 text-[12px] font-semibold text-slate-700 bg-white min-w-[85px] text-center shadow-sm">
                                            {calendarMonth.format('MMMM YYYY')}
                                        </div>
                                        <button 
                                            className={`w-7 h-7 rounded-lg border border-slate-200 text-slate-600 transition-colors bg-white shadow-sm flex items-center justify-center ${isDisabled ? 'opacity-40 cursor-not-allowed' : 'hover:bg-slate-50 cursor-pointer'}`} 
                                            onClick={nextMonth}
                                            disabled={isDisabled}
                                            title={isDisabled ? "Loading..." : "Next Month"}
                                        >
                                            <ChevronRight className="w-4 h-4" />
                                        </button>
                                        <button 
                                            className={`px-3 h-7 ml-1 rounded-lg border border-slate-200 text-[11px] font-semibold transition-colors bg-white shadow-sm flex items-center justify-center ${isDisabled || calendarMonth.isSame(moment(), 'month') ? 'text-slate-400 opacity-40 cursor-not-allowed' : 'text-blue-600 hover:bg-slate-50 cursor-pointer'}`} 
                                            onClick={goToToday}
                                            disabled={isDisabled || calendarMonth.isSame(moment(), 'month')}
                                            title={isDisabled ? "Loading..." : (calendarMonth.isSame(moment(), 'month') ? "Current month already selected" : "Go to current month")}
                                        >
                                            Today
                                        </button>
                                    </div>
                                </div>
                                {selectedEmp ? (
                                    <>
                                        <div className="flex-1 flex flex-col">
                                            {/* Employee Details and Stats Row */}
                                        <div className="flex flex-wrap items-center justify-between gap-3 mb-5 pb-4 border-b border-slate-100">
                                            {/* Left side: Name and Emp Code */}
                                            <div className="flex-1 min-w-[180px] flex items-center gap-3 overflow-hidden mr-2">
                                                <div className="shrink-0">
                                                    <UserAvatar 
                                                        name={selectedEmp.empName} 
                                                        imageUrl={selectedEmp.imgPath ? `${process.env.REACT_APP_BASE_URL}/uploads/${selectedEmp.imgPath}` : null} 
                                                        size={42} 
                                                    />
                                                </div>
                                                <div className="flex flex-col min-w-0">
                                                    <h4 className="text-[15px] font-bold text-slate-800 leading-tight m-0 truncate">{selectedEmp.empName}</h4>
                                                    <div className="text-[12px] text-slate-500 font-medium mt-0.5 truncate">
                                                        {selectedEmp.emp_id}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Right side: Month Stats */}
                                            {(() => {
                                                let subCount = 0;
                                                let missCount = 0;
                                                const startOfMonth = calendarMonth.clone().startOf('month');
                                                const endOfMonth = calendarMonth.clone().endOf('month');
                                                let day = startOfMonth.clone();
                                                while (day.isSameOrBefore(endOfMonth)) {
                                                    const st = empCalData.statuses[day.format('YYYY-MM-DD')];
                                                    if (st === 'P') subCount++;
                                                    if (st === 'A') missCount++;
                                                    day.add(1, 'day');
                                                }
                                                return (
                                                    <div className="flex items-center gap-3 shrink-0">
                                                        <div className="flex items-center justify-center gap-2 bg-green-50 text-green-700 px-3 py-1 rounded-full border border-green-100 min-w-[50px] shadow-sm" title={`${subCount} Submitted`}>
                                                            <div className="w-2 h-2 rounded-full bg-green-500 shrink-0"></div>
                                                            <span className="text-[13px] font-bold">{subCount}</span>
                                                        </div>
                                                        <div className="flex items-center justify-center gap-2 bg-red-50 text-red-700 px-3 py-1 rounded-full border border-red-100 min-w-[50px] shadow-sm" title={`${missCount} Missing`}>
                                                            <div className="w-2 h-2 rounded-full bg-red-500 shrink-0"></div>
                                                            <span className="text-[13px] font-bold">{missCount}</span>
                                                        </div>
                                                    </div>
                                                );
                                            })()}
                                        </div>

                                        <div className="flex-1 flex flex-col mb-2 min-h-0 relative">
                                            {isDisabled && (
                                                <div className="absolute inset-0 bg-white/75 backdrop-blur-[1px] z-30 flex flex-col items-center justify-center gap-2.5 rounded-lg">
                                                    <div className="w-10 h-10 border-[3.5px] border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                                                    <span className="text-[13px] font-semibold text-slate-700">Loading timeline...</span>
                                                </div>
                                            )}
                                            <div className="grid grid-cols-7 gap-y-3 mb-2">
                                                {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(d => (
                                                    <div key={d} className="text-center text-[12px] font-bold text-slate-800">
                                                        {d}
                                                    </div>
                                                ))}
                                            </div>
                                            <div className="grid grid-cols-7 gap-0 gap-x-2 text-center flex-1" style={{ gridAutoRows: '1fr' }}>
                                                {renderCalendarDays()}
                                            </div>
                                        </div>
                                        </div>

                                        <div className="mt-4 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 pt-4 border-t border-slate-100 min-h-[64px]">
                                            <div className="flex items-center gap-2">
                                                <div className="w-5 h-5 rounded-full bg-green-600 text-white flex items-center justify-center text-[9px] font-bold">P</div>
                                                <div className="text-[11px] font-semibold text-slate-600 flex flex-col items-start leading-tight"><span>Submitted</span><span>(Present)</span></div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <div className="w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center text-[9px] font-bold">A</div>
                                                <span className="text-[11px] font-semibold text-slate-600">Missing DSR</span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <div className="w-5 h-5 rounded-full bg-orange-500 text-white flex items-center justify-center text-[9px] font-bold">L</div>
                                                <span className="text-[11px] font-semibold text-slate-600">On Leave</span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <div className="w-5 h-5 rounded-full bg-purple-500 text-white flex items-center justify-center text-[9px] font-bold">H</div>
                                                <span className="text-[11px] font-semibold text-slate-600">Holiday</span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <div className="w-5 h-5 rounded-full bg-slate-100 text-slate-400 flex items-center justify-center text-[9px] font-bold">-</div>
                                                <span className="text-[11px] font-semibold text-slate-600">No Data</span>
                                            </div>
                                        </div>
                                    </>
                                ) : (
                                    <div className="p-8 text-center text-sm font-medium text-gray-400">
                                        Please select an employee from the table.
                                    </div>
                                )}
                            </div>
                        );
                    })()}
                </div>
            </div>
        </div>
    );
}

export default DSRAbsentSA;